import React, { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { Chip, IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import { useLazyListGeneralLedgerQuery } from "store/services/generalLedgerApi";

import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useAppMeta, useTablePagination } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import GeneralLedgerFilter from "./GeneralLedgerfilter";
import HighlightedText from "components/Shared/HighlightedText";

// ─── Payer Badge ────────────────────────────────────────────────────────────

const PAYER_COLOR = {
  VENDOR: { color: "primary" },
  RECEIVER: { color: "success" },
  SENDER: { color: "warning" },
};

const PayerBadge = ({ payer }) => {
  if (!payer) return <span className="text-gray-300 text-xs">—</span>;
  const { color } = PAYER_COLOR[payer] || { color: "default" };
  return (
    <Chip
      label={payer}
      color={color}
      size="small"
      sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}
    />
  );
};

// Accounting Summary 

// const AccountingSummary = ({ totalDebit, totalCredit, totalTransactions }) => {
//   const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
//   const diff = Math.abs(totalDebit - totalCredit);

//   return (
//     <div className="mb-4 rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden dark:border-gray-700 dark:bg-gray-800">
//       <div className="bg-[#3B5998] px-4 py-3 flex items-center justify-between text-white">
//         <div className="flex items-center gap-3">
//           <AccountBalance sx={{ fontSize: 28, color: "white" }} />
//           <div>
//             <div className="font-semibold text-lg leading-tight">Accounting Summary</div>
//             <div className="text-xs text-blue-100 mt-0.5">Double-entry bookkeeping totals</div>
//           </div>
//         </div>
//         {isBalanced ? (
//           <span className="bg-[#4CAF50] text-white text-xs px-2.5 py-1 rounded-sm font-bold tracking-wide">
//             ✓ BALANCED
//           </span>
//         ) : (
//           <span className="bg-red-500 text-white text-xs px-2.5 py-1 rounded-sm font-bold tracking-wide">
//             UNBALANCED
//           </span>
//         )}
//       </div>
//       <div className="flex flex-col md:flex-row bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
//         <div className="flex-1 p-5 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 relative">
//           <div className="text-gray-500 text-[11px] font-bold tracking-widest mb-2 flex items-center gap-1.5 uppercase">
//             <TrendingUp fontSize="small" className="text-green-500" /> TOTAL DEBIT
//           </div>
//           <div className="text-[26px] font-bold text-green-600 dark:text-green-500 mb-1">
//             NPR {totalDebit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
//           </div>
//           <div className="text-[11px] text-gray-400">Assets & Expenses Increased</div>
//           <div className="mt-4 text-xs font-semibold text-green-600">
//             Difference: NPR {diff.toFixed(2)}
//           </div>
//         </div>
//         <div className="flex-1 p-5 relative">
//           <div className="text-gray-500 text-[11px] font-bold tracking-widest mb-2 flex items-center gap-1.5 uppercase">
//             <TrendingDown fontSize="small" className="text-red-500" /> TOTAL CREDIT
//           </div>
//           <div className="text-[26px] font-bold text-red-600 dark:text-red-500 mb-1">
//             NPR {totalCredit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
//           </div>
//           <div className="text-[11px] text-gray-400">Liabilities, Equity & Revenue</div>
//           <div className="mt-4 text-xs text-gray-400 text-right md:absolute md:bottom-5 md:right-5 md:mt-0">
//             {totalTransactions} total transactions
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// ─── List Screen

const GeneralLedgerListScreen = () => {
  const [searchParams] = useSearchParams();
  const { params } = useCustomParams();

  const keyword = searchParams.get("search") || "";
  const fromDate = params.fromDate || "";
  const toDate = params.toDate || "";
  const serviceChargePayer = params.serviceChargePayer || "";
  const event = params.event || "";
  const glAccount = params.glAccount || "";

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const breadcrumbs = [
    { title: "General Ledger" },
  ];

  const columns = useMemo(
    () => [
      // {
      //   name: "GL. Number",
      //   selector: (row) => row.glNumber,
      //   cell: (row) => (
      //     <span>
      //       <HighlightedText text={row.glNumber || "N/A"} highlight={keyword} />
      //     </span>
      //   ),
      //   width: "140px",
      // },
      {
        name: "COC ID",
        selector: (row) => row.cocCollection?.cocCollectionId || "######",
        cell: (row) => (
          <span>
            <HighlightedText text={row.cocCollection?.cocCollectionId || "######"} highlight={keyword} />
          </span>
        ),
        width: "140px",
      },
      {
        name: "COD ID",
        selector: (row) => row.codTransfer?.codTransferId || "######",
        cell: (row) => (
          <span>
            <HighlightedText text={row.codTransfer?.codTransferId || "######"} highlight={keyword} />
          </span>
        ),
        width: "140px",
      },
      {
        name: "Delivery ID",
        selector: (row) => row.deliveryCollection?.deliveryCollectionId || "######",
        cell: (row) => (
          <span>
            <HighlightedText text={row.deliveryCollection?.deliveryCollectionId || "######"} highlight={keyword} />
          </span>
        ),
        width: "140px",
      },
      {
        name: "Shipment",
        selector: (row) => {
          const shipment = Array.isArray(row.shipment)
            ? row.shipment[0]
            : row.shipment;
          return shipment?.shipmentId;
        },
        cell: (row) => {
          const shipment = Array.isArray(row.shipment)
            ? row.shipment[0]
            : row.shipment;
          return (
            <span className="text-[13px] font-medium font-mono">
              <HighlightedText
                text={shipment?.shipmentId || "N/A"}
                highlight={keyword}
              />
            </span>
          );
        },
        width: "150px",
      },
      {
        name: "Transaction Description",
        selector: (row) => row.events,
        cell: (row) => (
          <span className="text-[13px]">
            <HighlightedText text={row.events || "N/A"} highlight={keyword} />
          </span>
        ),
        wrap: true,
        grow: 1,
      },
      {
        name: "Payer",
        selector: (row) => row.serviceChargePayer,
        cell: (row) => <PayerBadge payer={row.serviceChargePayer} />,
        width: "120px",
      },
      {
        name: "Created At",
        selector: (row) => row.createdAt,
        cell: (row) => (
          <div className="flex flex-col text-[12px] py-2">
            <span>{moment(row.createdAt).format("MMM DD, YYYY")}</span>
            <span className="mt-0.5">{moment(row.createdAt).format("hh:mm A")}</span>
          </div>
        ),
        width: "120px",
      },
      // Account column — stacked lines
      {
        name: "GL/Account",
        cell: (row) => (
          <div className="flex flex-col gap-1 py-2 text-[12px] w-full">
            {row.debits?.map((d, i) => (
              <div key={i}>
                <span className="text-gray-400 font-normal mr-1">Dr</span>
                <span className="font-semibold">{d.account}</span>
              </div>
            ))}
            <div className="border-t border-gray-700 dark:border-gray-100 w-full my-1" />
            {row.credits?.map((c, i) => (
              <div key={i}>
                <span className="text-gray-400 font-normal mr-1">Cr</span>
                <span className="font-semibold">{c.account}</span>
              </div>
            ))}
          </div>
        ),
        width: "220px",
      },

      // Debit column — stacked amounts
      {
        name: "Debit",
        right: true,
        cell: (row) => (
          <div className="flex flex-col gap-1 py-2 text-[12px] text-right w-full">
            {row.debits?.map((d, i) => (
              <div key={i} className="text-green-600 font-bold">
                NPR {d.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            ))}
            <div className="border-t border-gray-700 dark:border-gray-100 w-full my-1" />
            {row.credits?.map((_, i) => (
              <div key={i} className="text-gray-300">NPR 0.00</div>
            ))}
          </div>
        ),
        width: "130px",
      },

      // Credit column — stacked amounts
      {
        name: "Credit",
        right: true,
        cell: (row) => (
          <div className="flex flex-col gap-1 py-2 text-[12px] text-right w-full">
            {row.debits?.map((_, i) => (
              <div key={i} className="text-gray-300">NPR 0.00</div>
            ))}
            <div className="border-t border-gray-500 dark:border-gray-100 w-full my-1" />
            {row.credits?.map((c, i) => (
              <div key={i} className="text-red-500 font-bold">
                NPR {c.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            ))}
          </div>
        ),
        width: "130px",
      },
      {
        name: "Actions",
        cell: (row) => (
          <Link to={`/app/crm/general-ledger/${row._id}/detail`}>
            <IconButton size="small" color="primary">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Link>
        ),
        right: true,
        width: "80px",
        ignoreRowClick: true,
      },
    ],
    [keyword]
  );

  const [fetchGLs, { data: glRes, isLoading, isFetching, error }] =
    useLazyListGeneralLedgerQuery();

  useEffect(() => {
    fetchGLs({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(serviceChargePayer && { serviceChargePayer }),
      ...(event && { event }),
      ...(glAccount && { glAccount }),
    });
  }, [fetchGLs, currentPage, perPage, keyword, fromDate, toDate, serviceChargePayer, event, glAccount]);

  useAppMeta("General Ledger");

  const totalItems = glRes?.totalItems || 0;

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper className="!px-6 !py-5">

        {/* Filters */}
        <GeneralLedgerFilter />
        <AppTable
          columns={columns}
          data={glRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={totalItems}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />

      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default GeneralLedgerListScreen;
